import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Report, Result, SensitivityAnalysisParameter } from '@/interfaces/SensitivityAnalysisReport';

type Dependencies = {
  http: {
    get: <Type>(url: string) => Promise<{ data: Type }>
    post: <Type>(url: string, payload?: unknown) => Promise<{ data: Type }>
  },
  errorHandler: (error: unknown) => void
}

type Progress = {
  size: number
  progress: number
}

export const buildSensitivityAnalysisModule = (dependencies: Dependencies) => {
  const { http, errorHandler } = dependencies;

  @Module({ namespaced: true })
  class SensitivityAnalysisModule extends VuexModule {
    private _progress: Progress | null = null;
    private _results: Record<string, Report> = {};

    @Action
    public async start() {
      const scenario = this.context.rootGetters['scenario/getFields'];
      this.context.dispatch('overlay/enable', {
        progress: 'sensitivityAnalysis/progress',
        cancel: 'sensitivityAnalysis/cancel'
      }, { root: true });
      try {
        const { data: params } : { data: any} = await http.get(`/scenario/${scenario.uuid}/sensitivityAnalysis/default`);
        if (params) {
          const sensitivityParameter: Array<SensitivityAnalysisParameter> = params.params;
          if (sensitivityParameter && Array.isArray(sensitivityParameter)) {
            sensitivityParameter.forEach((p: SensitivityAnalysisParameter) => {
              if (p.labelKey === 'interest') {
                p.op = 'add';
                p.max = 0.01;
                p.min = -0.01;
                p.step = 0.01;
              }
            });
            params.params = sensitivityParameter;
          }
        }
        await http.post(`/scenario/${scenario.uuid}/sensitivityAnalysis/start`, params);
        this.context.dispatch('loadProgress');
      } catch (error) {
        this.context.dispatch('overlay/disable', { withCancel: false }, { root: true });
        errorHandler(error);
      }
    }

    @Action
    public async cancel() {
      const scenario = this.context.rootGetters['scenario/getFields'];
      try {
        await http.get(`/scenario/${scenario.uuid}/sensitivityAnalysis/cancel`);
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public async loadProgress() {
      const scenario = this.context.rootGetters['scenario/getFields'];
      try {
        const { data } = await http.get<Progress>(`/scenario/${scenario.uuid}/sensitivityAnalysis/progress`);
        this.context.commit('setProgress', { id: scenario.uuid, data });
        if (data.progress === data.size) {
          await this.context.dispatch('overlay/disable', { withCancel: false }, { root: true });
          await this.context.dispatch('loadResult');
        } else if (data.progress < data.size) {
          setTimeout(() => this.context.dispatch('loadProgress'), 500);
        }
      } catch (error) {
        await this.context.dispatch('overlay/disable', { withCancel: false }, { root: true });
        this.context.commit('setProgress', { id: scenario.uuid, data: null });
      }
    }

    @Action
    public async loadResult() {
      const scenario = this.context.rootGetters['scenario/getFields'];
      try {
        const { data } = await http.get<Result>(`/scenario/${scenario.uuid}/sensitivityAnalysis/result`);
        this.context.commit('setResult', { id: scenario.uuid, data });
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public async invalidate(): Promise<void> {
      this.context.commit('setResult', { id: this.currentScenarioId, data: null });
    }

    @Mutation
    private setProgress(progress: { id: string, data: Progress }) {
      this._progress = progress.data;
    }

    @Mutation
    private setResult({ id, data }: { id: string, data: Report }) {
      this._results = {
        ...this._results,
        [id]: data
      };
    }

    get result() {
      const scenario = this.context.rootGetters['scenario/getFields'];
      return this._results[scenario.uuid]?.params ?? null;
    }

    get progress() {
      return this._progress;
    }

    get currentScenarioId(): string {
      const scenario = this.context.rootGetters['scenario/getFields'];
      return scenario.uuid;
    }
  }

  return SensitivityAnalysisModule;
};
